import { DrawerHeader } from "../../../../components/drawer/drawer-header";
import moment from "moment";
import { Icon } from "../../../../components/icon/icon";
import {
  FormHeaderTextarea,
  FormTextarea,
  FormFieldName,
  FormSelect
} from "../../../../components/form/form";
import { InputList } from "./input-list";
import { memo, useEffect, useState } from "react";
import { useProfileEdit } from "./profile-edit.hook";
import { Drawer } from "../../../../components/drawer/drawer";
import { Form } from "antd";
import { useTranslation } from "react-i18next";
import ApiProfileManagement from "../../../../api/profile-management";
import { TableCell } from "../../../../components/table/table-cell/table-cell";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import DropdownFilters from "../../../../components/dropdown-filters/dropdown-filters";
import ApiSubjectsManagement from "../../../../api/subjects-manager";

const SELECT_TYPE_OPTIONS = [
  { label: "Single choice", value: "message:single" },
  { label: "Multi choice", value: "message:multi" },
  { label: "Text input", value: "message:input" },
  { label: "Picker", value: "message:picker" },
  { label: "Slider", value: "message:slider" },
  { label: "Date", value: "message:date" },
  { label: "Time", value: "message:time" },
];

const DataTable = ({ data, fetchData, hasMore, updateFilterValue, subjectList }) => {
  const { t } = useTranslation();
  const columns = [
    {
      title: t("subject_management.profile.table.subject_id"),
      dataIndex: "subjectId",
      render: (_, profile) => {
        return <TableCell text={profile.displayId} />;
      },
      key: "subjectId",
      width: "20%",
    },
    {
      title: t("subject_management.profile.table.response"),
      dataIndex: "response",
      render: (_, profile) => {
        return <TableCell text={profile.value?.label || profile.value?.value || profile.value} />;
      },
      key: "response",
      width: "40%",
    },
    {
      title: t("subject_management.profile.table.completed_at"),
      dataIndex: "completedAt",
      render: (_, profile) => {
        return (
          <TableCell
            text={
              `${moment(profile.completedAt)
                .utc()
                .format("DD MMM YYYY, HH:mm")}, UTC` || "N/A"
            }
          />
        );
      },
      key: "completedAt",
      width: "40%",
    }
  ];
  return (
    <>
      <div style={{ marginTop: 32, marginBottom: 16, fontSize: 14 }}>
        <b>Responses</b>
      </div>
      <DropdownFilters
        filtersBy={[
          { name: "Subject", id: "subjectIds", children: subjectList },
        ]}
        updateFilterValue={updateFilterValue}
      />
      <div style={{ padding: 6 }}/>
      <InfinityTable
        data={data}
        fetchData={fetchData}
        hasMore={hasMore}
        tableColumns={columns}
        isLoading={false}
      />
    </>
  );
};

const ProfileCardInside = memo(
  ({
    profile,
    close,
    editMode,
    createProfile,
    updateProfile,
    cancelEditing,
    deleteProfile,
    toEditMode,
    onlyView,
    scenarioData,
    reloadResponses
  }) => {
    const { t } = useTranslation();
    const control = useProfileEdit({
      profile,
      createProfile,
      updateProfile: async (...args) => {
        await updateProfile(...args);
        setTimeout(() => {
          reloadResponses();
        }, 500);
      },
      cancelEditing,
      close,
    });

    return (
      <>
        <DrawerHeader
          title={<p className="life-fact-title">{t("subject_management.profile.title")}</p>}
          isEditing={editMode}
          onEdit={onlyView ? undefined : toEditMode}
          onClose={close}
          onSave={control.save}
          onCancel={control.cancel}
          menu={
            onlyView
              ? undefined
              : {
                  items: [
                    {
                      label: "Delete",
                      icon: (
                        <Icon
                          name={"delete"}
                          style={{ marginRight: 8, marginLeft: -4 }}
                        />
                      ),
                      onClick: deleteProfile,
                    },
                  ]
                }
          }
        />
        <div style={{ paddingBottom: 24 }} >
          <FormHeaderTextarea
            readonly={!editMode}
            placeholder={"Title"}
            defaultValue={control.title.value}
            onChange={control.title.update}
            maxLength={100}
          />
        </div>
        <Form.Item name="description" initialValue={control.description.value}>
          <FormFieldName text="Description:" />
          <FormTextarea
            readonly={!editMode}
            placeholder={"Description"}
            defaultValue={control.description.value}
            onChange={control.description.update}
            maxLength={1000}
            style={{
              fontSize: 14,
              padding: 0
            }}
          />
        </Form.Item>
        <Form.Item name="taskType" initialValue={control.inputType.value}>
          <FormFieldName text="Input type:" />
          <FormSelect
            options={SELECT_TYPE_OPTIONS}
            style={{ minWidth: 180 }}
            placeholder="Select input type"
            value={control.inputType.value}
            onChange={control.inputType.update}
            readonly={!editMode}
            disabled={editMode && control.boundTo.value.length > 0}
            getPopupContainer={trigger => trigger.parentNode}
          />
        </Form.Item>
        <Form.Item name="displayRule" initialValue={control.displayRule.value}>
          <FormFieldName text="Display rule:" />
          <FormSelect
            style={{ minWidth: 220 }}
            options={[
              { label: 'Always', value: 'always' },
              { label: 'Only when data is available', value: 'onlyWhenData' },
            ]}
            value={control.displayRule.value}
            onChange={control.displayRule.update}
            readonly={!editMode}
            getPopupContainer={trigger => trigger.parentNode}
          />
        </Form.Item>
        <InputList
          scenarioData={scenarioData}
          inputType={control.inputType.value}
          boundTo={control.boundTo.value}
          onChange={control.boundTo.update}
          readonly={!editMode}
        />
      </>
    );
  }
);

const ProfileCard = ({
  profile,
  isOpen,
  close,
  editMode,
  createProfile,
  updateProfile,
  deleteProfile,
  toEditMode,
  cancelEditing,
  onlyView,
  scenarioData,
  subjectId
}) => {
  const [profileFieldData, setProfileFieldData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({});
  const [subjectList, setSubjectList] = useState([]);

  useEffect(() => {
    fetchSubjectList();
  }, []);

  const fetchSubjectList = async () => {
    ApiSubjectsManagement.getSubjectsSimple().then((r) => {
      setSubjectList(
        r.data.subjects.map((s) => ({
          id: s.id,
          name: s.displayId,
        }))
      );
    });
  };

  const fetchMoreData = (customPage) => {
    const nextPage = customPage || page;
    ApiProfileManagement.getProfileData({
      subjectProfileFieldId: profile.id,
      page: nextPage,
      subjectId,
      subjectIds: filters.subjectIds || [],
    }).then((res) => {
      setProfileFieldData(prev => [...prev, ...res.data.data]);
      setHasMore(res.data.hasMore);
      setPage(p => p + 1);
    });
  };

  const updateFilterValue = (newFilters) => {
    setFilters(newFilters);
  };

  // useEffect(() => {
  //   if (profile.id) {
  //     setProfileFieldData([]);
  //     setPage(1);
  //     setHasMore(true);
  //     fetchMoreData(1);
  //   }
  // }, [filters]);

  // useEffect(() => {
  //   setProfileFieldData([]);
  //   if (profile.id) {
  //     setPage(1);
      
  //     setHasMore(true);
  //     fetchMoreData(1);
  //   }
  // }, [profile.id]);

  useEffect(() => {
    if (profile.id) {
      setProfileFieldData([]);
      setPage(1);
      setHasMore(true);
      fetchMoreData(1);
    }
  }, [profile.id, JSON.stringify(filters)]);

  const reloadResponses = () => {
    setPage(1);
    setProfileFieldData([]);
    setHasMore(true);
    fetchMoreData(1);
  };

  return (
    <Drawer open={isOpen} size={800} closable={false} onClose={close}>
      <div style={{ paddingBottom: 48 }}>
        {isOpen && (
          <>
            <ProfileCardInside
              scenarioData={scenarioData}
              profile={profile}
              createProfile={createProfile}
              close={close}
              cancelEditing={cancelEditing}
              updateProfile={updateProfile}
              deleteProfile={deleteProfile}
              toEditMode={toEditMode}
              editMode={editMode}
              onlyView={onlyView}
              reloadResponses={reloadResponses}
            />
            <DataTable
              updateFilterValue={updateFilterValue}
              data={profileFieldData}
              fetchData={fetchMoreData}
              hasMore={hasMore}
              subjectList={subjectList}
            />
          </>
        )}
      </div>
    </Drawer>
  );
};

export default ProfileCard;
